import { createFailure, FailureSeverity, UnexpectedError, validationFailed } from "sprint-common-web";

import { useMonitoringApi } from "../../common/api/monitoring-api/useMonitoringApi";
import { useEncodeSpecificationApi } from "../../common/api/orders-api/encode-specification/useEncodeSpecificationApi";
import { useMuxSpecificationApi } from "../../common/api/orders-api/mux-specification/useMuxSpecificationApi";
import { useNoteApi } from "../../common/api/orders-api/notes/useNoteApi";
import { useOrderOutputApi } from "../../common/api/orders-api/order-output/useOrderOutputApi";
import { useOrderProxyApi } from "../../common/api/orders-api/proxy/useOrderProxyApi";
import { useOnPremOrdersApi } from "../../common/api/orders-api/useOnPremOrdersApi";
import { useOrdersRequestsApi } from "../../common/api/orders-api/useOrderRequestsApi";
import { useOrdersApi } from "../../common/api/orders-api/useOrdersApi";

export const useOrdersModuleApi = () => {
    const {
        getOrderList,
        validateCloudAssets,
        createOrder,
        getOrderDetails,
        validateOrderPONumber,
        validateDeliveryPath,
        approveOrderDelivery,
        approveOrderDeliveries,
        exportOrders,
        rejectOrder,
        completeRejectedOrder,
        deleteOrder,
        copyOrderOutputs,
        updateOrderDeliveryFolder,
        requestOrderWatermark,
        removeOrderWatermark,
        validateIfOrderHasWatermarkRule,
        reprocessOrder,
        selectRelevantVideo,
        mergeOrders,
        createCloudOrderFromRequest,
        getCloudOrderAuditList
    } = useOrdersApi();
    const {
        getOnPremOrderList,
        updateOnPremOrderStatus,
        exportOnPremOrdersForProcessing,
        exportOnPremOrders,
        getOnPremOrderAuditList
    } = useOnPremOrdersApi();
    const {
        getOrderRequestList,
        refreshOrderRequest,
        getOrderRequestDetails,
        moveOrderRequestToPending,
        validateMoveOrderRequestToPending
    } = useOrdersRequestsApi();
    const { getExecutions } = useMonitoringApi();
    const { getProxiesByOrder } = useOrderProxyApi();
    const { getOrderOutputsByOrder } = useOrderOutputApi();
    const { getEncodeSpecificationById } = useEncodeSpecificationApi();
    const { getMuxSpecificationById } = useMuxSpecificationApi();
    const { addOrderNote, getOrderNotes } = useNoteApi();

    const validateDeliveryPathAndHandleErrors = async (deliveryPath: string, isAdmin: boolean) => {
        try {
            return await validateDeliveryPath({ deliveryPath });
        } catch (err) {
            console.log(err);
            if (err instanceof UnexpectedError && err.type === "SmartjogError") {
                const smartjogFailure = isAdmin
                    ? createFailure(
                          "Smartjog API error: " + err.message,
                          "smartjog-availability",
                          FailureSeverity.Error
                      )
                    : createFailure(
                          "It seems that Smartjog API is not available at the moment. Please try later",
                          "smartjog-availability",
                          FailureSeverity.Error
                      );
                return validationFailed([smartjogFailure]);
            }

            const failure = createFailure(
                "Unexpected error has occured. Contact support team",
                "unexpected-error",
                FailureSeverity.Error
            );

            return validationFailed([failure]);
        }
    };

    return {
        getOrderList,
        validateCloudAssets,
        createOrder,
        approveOrderDelivery,
        approveOrderDeliveries,
        getExecutions,
        getProxiesByOrder,
        getOrderOutputsByOrder,
        getEncodeSpecificationById,
        getMuxSpecificationById,
        getOrderDetails,
        validateOrderPONumber,
        validateDeliveryPath: validateDeliveryPathAndHandleErrors,
        validateMoveOrderRequestToPending,
        exportOrders,
        rejectOrder,
        completeRejectedOrder,
        deleteOrder,
        copyOrderOutputs,
        getOrderRequestList,
        getOnPremOrderList,
        updateOnPremOrderStatus,
        refreshOrderRequest,
        getOrderRequestDetails,
        moveOrderRequestToPending,
        exportOnPremOrders,
        exportOnPremOrdersForProcessing,
        updateOrderDeliveryFolder,
        getOnPremOrderAuditList,
        addOrderNote,
        getOrderNotes,
        requestOrderWatermark,
        removeOrderWatermark,
        validateIfOrderHasWatermarkRule,
        reprocessOrder,
        selectRelevantVideo,
        mergeOrders,
        getCloudOrderAuditList,
        createCloudOrderFromRequest
    };
};
